import axios from "axios";
import * as moment from "moment";
import { lightFormat } from "date-fns";

import Utils, { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import { AuditLogEntryDto, parseAuditLogsModel } from "../../audit/model";

import {
    EmployeeRecordDetailModel,
    DeleteEmployeeRecordResult,
    parseEmployeeModel,
    MergeEmployeeRecordModel,
    EmployeeRecordSearchModel,
    EditEmployeeRecordFromOhCaseModel
} from "./model";
import { EmployeeRecordFormModel } from "./schema";

class EmployeeRecordApi {

    private readonly apiUrl = "/api/EmployeeRecord";

    public async detail(id: string, customerId: string): Promise<EmployeeRecordDetailModel> {
        const response = await axios.get<ServerModelWithDateStrings<EmployeeRecordDetailModel>>(`${this.apiUrl}/${id}`, { params: { customerId } });
        const model = response.data;
        return parseEmployeeModel(model);
    }

    public async save(employeeRecord: EmployeeRecordFormModel): Promise<EmployeeRecordDetailModel> {
        const query = "?parameters=" + Utils.getParameters();
        const response = await axios.put<
            ServerModelWithDateStrings<EmployeeRecordDetailModel>
        >(`${this.apiUrl}/${employeeRecord.id}${query}`, this.encodeModel(employeeRecord));
        const model = response.data;
        return parseEmployeeModel(model);
    }

    public async saveFromOhCase(customerId: string, employeeRecord: EditEmployeeRecordFromOhCaseModel): Promise<EmployeeRecordDetailModel> {
        const response = await axios.put<
            ServerModelWithDateStrings<EmployeeRecordDetailModel>
        >(`/api/customer/${customerId}/employeeRecord/${employeeRecord.id}/fromOhCase`, this.encodeModelMoments(employeeRecord));
        const model = response.data;
        return parseEmployeeModel(model);
    }

    public async create(employeeRecord: EmployeeRecordFormModel): Promise<EmployeeRecordDetailModel> {
        const query = "?parameters=" + Utils.getParameters();
        const response = await axios.post<ServerModelWithDateStrings<EmployeeRecordDetailModel>>(`${this.apiUrl}${query}`, this.encodeModel(employeeRecord));
        const model = response.data;
        return parseEmployeeModel(model);
    }

    public async delete(id: any): Promise<DeleteEmployeeRecordResult> {
        const query = "?parameters=" + Utils.getParameters();
        const response = await axios.delete(`${this.apiUrl}/${id}${query}`);
        return response.data;
    }

    public activate(customerId: string, id: string): Promise<void> {
        return axios.post(`/api/customer/${customerId}/employeerecord/${id}/activate`);
    }

    public deactivate(customerId: string, id: string): Promise<void> {
        return axios.post(`/api/customer/${customerId}/employeerecord/${id}/deactivate`);
    }

    public async searchEmployeeRecords(customerId: string, employeeName: string): Promise<EmployeeRecordSearchModel[]> {
        const response = await axios.get<EmployeeRecordSearchModel[]>(`/api/employeerecord/customer/${customerId}/employeeName/${employeeName}`);
        return response.data;
    }

    public async searchEmployeeRecordsOrGetAllEmployees(customerId: string, employeeName: string): Promise<EmployeeRecordSearchModel[]> {
        const response = await axios.post<EmployeeRecordSearchModel[]>(`/api/employeerecord/customer/${customerId}/employeeName`, { employeeName });
        return response.data;
    }

    public async getAuditLogs(customerId: string, employeeRecordId: string): Promise<AuditLogEntryDto[] | null> {
        const response = await axios.get(`/api/customer/${customerId}/EmployeeRecord/${employeeRecordId}/auditLogs`);
        const result = Utils.CheckResponse<ServerModelWithDateStrings<AuditLogEntryDto[]>>(response.data);

        if (!result)
        {
            return null;
        }

        return result.map(parseAuditLogsModel);
    }

    public mergeEmployeeRecord(model: MergeEmployeeRecordModel): Promise<void> {
        return axios.post("/api/employeerecord/merge", model);
    }

    private encodeModelMoments(model: EditEmployeeRecordFromOhCaseModel) {
        return { ...model, dateOfBirth: model.dateOfBirth && moment(model.dateOfBirth).format("YYYY-MM-DD") };
    }

    private encodeModel(model: EmployeeRecordFormModel) {
        return { ...model, dateOfBirth: model.dateOfBirth ? lightFormat(model.dateOfBirth, "yyyy-MM-dd") : null };
    }

    public async setCloseMonitoring(customerId: string, employeeRecordId: string, closeMonitoring: boolean) {
        const url = `/api/customer/${customerId}/EmployeeRecord/${employeeRecordId}/closeMonitoring/${closeMonitoring}`;
        await axios.post(url);
    }
}

export const employeeRecordApi = new EmployeeRecordApi();
